<template>
    <LoadingCard v-if="loading" />
    <div v-else>
        <div class="toolbar">
            <div class="toolbar__label">
                Fertilisers available for use in spray diaries
            </div>
            <div class="actions">
                <i class="actions__item zmdi zmdi-search" @click="enableSearch"></i>
                <i class="actions__item far fa-check-square" v-if="onlySelected" @click="onlySelected=false"
                   v-b-tooltip="'Showing only selected items'"></i>
                <i class="actions__item far fa-square" v-else @click="onlySelected=true"
                   v-b-tooltip="'Show only selected items'"></i>
            </div>

            <div class="toolbar__search" :class="searchEnabled ? 'd-block' : 'd-none'">
                <input type="text" placeholder="Search..." ref="searchInput" v-model="searchTerm">
                <i class="toolbar__search__close zmdi zmdi-long-arrow-left" @click="disableSearch"></i>
            </div>
        </div>
        <div class="row">
            <div v-for="group in fertiliserGroups" class="col-12">
                <div class="card">
                    <div class="card-header bg-primary text-white">
                        {{ group.name | title_format }}
                    </div>

                    <b-table small hover stacked="md" :items="group.items" :fields="table.fields"
                             sort-by="name" :show-empty="true" @row-clicked="toggleRow">

                        <template slot="name" slot-scope="row">
                            <div class="custom-control custom-checkbox">
                                <input class="custom-control-input"
                                       :id="`fertiliser${row.item.id}`" type="checkbox"
                                       :checked="fertiliserInUse(row.item.id)">
                                <label class="custom-control-label" :for="`fertiliser${row.item.id}`">{{ row.item.name }}</label>
                            </div>
                        </template>

                        <template slot="state" slot-scope="row">
                            <template v-if="row.item.is_liquid">
                                <span class="badge badge-light">Liquid</span>
                            </template>
                            <span class="badge badge-light" v-else>Solid</span>
                        </template>

                        <template slot="additives" slot-scope="row">
                            <div>
                                <template
                                        v-for="each_additive in [...row.item.additives].sort((a,b) => a.additive.name.localeCompare(b.additive.name))">
                                    <div class="badge-group mr-1">
                                        <span class="badge badge-secondary mb-1">{{ each_additive.additive.name }}</span>
                                        <span class="badge badge-warning mb-1">{{ each_additive.percentage }}%</span>
                                    </div>
                                </template>
                            </div>

                            <div class="badge-group" v-if="row.item.boron">
                                <span class="badge badge-secondary mb-1">B</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.boron }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.calcium">
                                <span class="badge badge-secondary mb-1">Ca</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.calcium }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.chlorine">
                                <span class="badge badge-secondary mb-1">Cl</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.chlorine }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.copper">
                                <span class="badge badge-secondary mb-1">Cu</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.copper }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.iron">
                                <span class="badge badge-secondary mb-1">Fe</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.iron }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.potassium">
                                <span class="badge badge-secondary mb-1">K</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.potassium }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.magnesium">
                                <span class="badge badge-secondary mb-1">Mg</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.magnesium }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.manganese">
                                <span class="badge badge-secondary mb-1">Mn</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.manganese }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.molybdenum">
                                <span class="badge badge-secondary mb-1">Mo</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.molybdenum }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.nitrogen">
                                <span class="badge badge-secondary mb-1">N</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.nitrogen }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.nickel">
                                <span class="badge badge-secondary mb-1">Ni</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.nickel }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.phosphorus">
                                <span class="badge badge-secondary mb-1">P</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.phosphorus }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.sulphur">
                                <span class="badge badge-secondary mb-1">S</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.sulphur }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.selenium">
                                <span class="badge badge-secondary mb-1">Se</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.selenium }}</span>
                            </div>
                            <div class="badge-group" v-if="row.item.zinc">
                                <span class="badge badge-secondary mb-1">Zn</span>
                                <span class="badge badge-purple text-white mb-1">{{ row.item.zinc }}</span>
                            </div>
                        </template>

                    </b-table>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import createLinkMutation from '@views/StockManagement/FertiliserManager/mutationCreateGrowerFertiliserLink.graphql';
import deleteLinkMutation from '@views/StockManagement/FertiliserManager/mutationDeleteGrowerFertiliserLink.graphql';
import listFertilisersQuery from '@views/StockManagement/FertiliserManager/queryListFertilisers.graphql';
import listLinkedFertilisersQuery from '@views/StockManagement/FertiliserManager/queryListGrowerLinkedFertilisers.graphql';

import groupBy from 'lodash.groupby';

/**
 * @property {String} searchTerm
 */
export default {
  name: 'FertiliserManager',
  apollo: {
    allFertilisers: {
      query: listFertilisersQuery,
      update: data => {
        console.log('data');
        return data.filterFertilisers.data;
      }
    },
    linkedFertilisers: {
      query: listLinkedFertilisersQuery,
      update: data => data.filterGrowerFertiliserLinks,
    },
  },
  computed: {
    table() {
      return {
        fields: [
          {
            key: 'name',
            label: 'Fertiliser',
            sortable: true,
          },
          {
            key: 'state',
            label: 'State',
          },
          {
            key: 'additives',
            label: 'Components',
          },
        ],
      };
    },
    fertiliserGroups() {
      const subjectMatchesSearch = subject => {
        return (
          (this.onlySelected === false || this.linkedFertilisers.find(link => link.id === subject.id)) &&
          (this.searchTerm.length === 0 || subject.name.toLowerCase().includes(this.searchTerm.toLowerCase()))
        );
      };

      const matchedData = [];
      const allData = groupBy(this.allFertilisers, fert => fert.type);
      Object.keys(allData).forEach(key => {
        const matchedItems = allData[key].filter(subjectMatchesSearch);

        if(matchedItems.length > 0) {
          matchedData.push({
            name: key,
            items: matchedItems.sort((a, b) => a.name.localeCompare(b.name))
          });
        }
      });

      return matchedData.sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  watch: {
    linkedFertilisers(fertilisers) {
      if (!this.fertilisersLoaded) {
        this.fertilisersLoaded = true;
        this.onlySelected = fertilisers.length > 0;
      }
    },
  },
  data() {
    return {
      allFertilisers: [],
      linkedFertilisers: [],
      fertilisersLoaded: false,
      searchEnabled: false,
      searchTerm: '',
      onlySelected: true,
      loading: 0,
    };
  },
  methods: {
    enableSearch() {
      this.searchEnabled = true;
      this.$nextTick(() => this.$refs.searchInput.focus());
    },
    disableSearch() {
      this.searchTerm = '';
      this.searchEnabled = false;
    },
    toggleRow(item, index, event) {
      event.preventDefault();
      event.stopPropagation();
      this.handleFertiliserToggle(item);
    },
    handleFertiliserToggle(fertiliser) {
      if (this.fertiliserInUse(fertiliser.id)) {
        this.unlinkFertiliser(fertiliser);
      } else {
        this.linkFertiliser(fertiliser);
      }
    },
    fertiliserInUse(id) {
      return !!this.linkedFertilisers.find(item => item.id === id);
    },
    unlinkFertiliser(fertiliser) {
      this.$apollo.mutate({
        mutation: deleteLinkMutation,
        variables: {
          input: {
            id: fertiliser.id,
          },
        },
        update: (store, { data: { deleteGrowerFertiliserLink: unlinkedFertiliser } }) => {
          const data = store.readQuery({ query: listLinkedFertilisersQuery });
          const indexToRemove = data.filterGrowerFertiliserLinks.findIndex(
            item => item.id === unlinkedFertiliser.id,
          );
          if (indexToRemove !== -1) {
            data.filterGrowerFertiliserLinks.splice(indexToRemove, 1);
            store.writeQuery({ query: listLinkedFertilisersQuery, data });
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          deleteGrowerFertiliserLink: {
            __typename: 'Fertiliser',
            ...fertiliser,
          },
        },
      });
    },
    linkFertiliser(fertiliser) {
      this.$apollo.mutate({
        mutation: createLinkMutation,
        variables: {
          input: {
            id: fertiliser.id,
          },
        },
        update: (
          store,
          {
            data: {
              createGrowerFertiliserLink: linkedFertiliser,
            },
          },
        ) => {
          const data = store.readQuery({
            query: listLinkedFertilisersQuery,
          });
          data.filterGrowerFertiliserLinks.push(
            linkedFertiliser,
          );
          store.writeQuery({ query: listLinkedFertilisersQuery, data });
        },
        optimisticResponse: {
          __typename: 'Mutation',
          createGrowerFertiliserLink: {
            __typename: 'Fertiliser',
            ...fertiliser,
          },
        },
      });
    },
  },
};
</script>
